import React from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { push } from 'react-router-redux'
import { history } from '../store'
import AppTimeHeader from '../containers/AppTimeHeader'
import { withNamespaces } from "react-i18next"
import StepBarTime from '../components/StepBarTime'
import { Phone, CreditCard } from 'lucide-react'
import ReactCountryFlag from 'react-country-flag'
import { postTimeCancelPrebooking, postTimeUpdateLockerInfo, postRedsys, fetchCustomer } from '../actions/qrstime'

const PREBOOKING_KEY = 'vacway_time_prebooking';
const PREBOOKING_TOTAL_SECONDS = 300;

import PHONE_PREFIXES from '../utils/phonePrefixes';

class PhoneTimeView extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      phonePrefix: '+34',
      phoneNumber: '',
      customerName: '',
      customerEmail: '',
      termsAccepted: false,
      prefixOpen: false,
      prefixSearch: '',
      isProcessingPayment: false,
      redsysSignature: null,
      redsysMerchant: null,
      redsysVersion: null,
      minutes: 5,
      seconds: 0,
      isTimeUp: false,
      showExitDialog: false,
      validationErrors: {},
      redsysAction: 'https://sis.redsys.es/sis/realizarPago',
    }
    this.prefixRef = React.createRef();
    this.redsysFormRef = React.createRef();
  }

  getButtonStyle() {
    const { qrData } = this.props;
    const buttonColor1 = qrData?.headerConfig?.buttonColor1 || '#01315f';
    const buttonColor2 = qrData?.headerConfig?.buttonColor2 || '#067ddb';
    const buttonText = qrData?.headerConfig?.buttonText || '#ffffff';

    return {
      background: `linear-gradient(135deg, ${buttonColor1}, ${buttonColor2})`,
      color: buttonText,
      border: 'none'
    };
  }

  getPrimaryColor() {
    const { qrData } = this.props;
    return qrData?.headerConfig?.primaryColor || '#01315f';
  }

  handlePhoneChange = (e) => {
    this.setState({ phoneNumber: e.target.value.replace(/\D/g, '') });
  }

  selectPrefix = (dial) => {
    this.setState({ phonePrefix: dial, prefixOpen: false, prefixSearch: '' });
  }

  togglePrefixDropdown = () => {
    this.setState(s => ({ prefixOpen: !s.prefixOpen }));
  }

  componentDidMount() {
    // Guard: if this locker already has a completed rental ticket, the user must not
    // re-enter the purchase flow (e.g. via browser back). Send them to the QR home view.
    const { lockerAssigned, qr } = this.props.location.state;
    const existingTicket = (() => { try { return JSON.parse(sessionStorage.getItem('vacway_time_ticket')); } catch { return null; } })();
    if (existingTicket && existingTicket.lockerAssigned === lockerAssigned) {
      history.replace(`/qrsale/${qr}`);
      return;
    }

    document.addEventListener('mousedown', this.handleOutsideClick);

    // Restore countdown from session if re-entering the view
    const session = (() => { try { return JSON.parse(sessionStorage.getItem(PREBOOKING_KEY)); } catch { return null; } })();
    if (session && session.lockerAssigned === lockerAssigned && session.startTimestamp) {
      const elapsed = Math.floor((Date.now() - session.startTimestamp) / 1000);
      const remaining = Math.max(PREBOOKING_TOTAL_SECONDS - elapsed, 0);
      if (remaining > 0) {
        this.setState({
          minutes: Math.floor(remaining / 60),
          seconds: remaining % 60,
        }, this.startCountdown);
      } else {
        this.setState({ isTimeUp: true });
        return;
      }
    } else {
      this.startCountdown();
    }

    // Block browser navigation (back/forward/close)
    this.unblockHistory = history.block((location, action) => {
      this.setState({ showExitDialog: true, pendingNavigation: { location, action } });
      return false;
    });

    window.addEventListener('beforeunload', this.handleBeforeUnload);
  }

  handleOutsideClick = (e) => {
    if (this.prefixRef.current && !this.prefixRef.current.contains(e.target)) {
      this.setState({ prefixOpen: false, prefixSearch: '' });
    }
  }

  handleTermsChange = (e) => {
    this.setState({ termsAccepted: e.target.checked });
  }

  getLockerType(type) {
    const names = {
      'BNS': 'Standard',    'CNS': 'Standard Carga',
      'BWS': 'Standard XL', 'CWS': 'Standard Carga XL',
      'BNM': 'Medium',      'CNM': 'Medium Carga',
      'BWM': 'Medium XL',   'CWM': 'Medium Carga XL',
      'BNL': 'Large',       'CNL': 'Large Carga',
      'BWL': 'Large XL',    'CWL': 'Large Carga XL',
      'BNXL': 'Large',      'CNXL': 'Large Carga',
      'BWXL': 'Large XL',   'CWXL': 'Large Carga XL',
      'BN2XL': 'Large',     'CN2XL': 'Large Carga',
      'BW2XL': 'Large XL',  'CW2XL': 'Large Carga XL',
      'BN3XL': 'Ski',       'CN3XL': 'Ski Carga',
      'BW3XL': 'Ski XL',    'CW3XL': 'Ski Carga XL',
    };
    return names[type] || type;
  }

  validateFields() {
    const { phoneNumber, customerName, customerEmail, termsAccepted } = this.state;
    const errors = {};
    if (customerName.trim().length < 2) errors.customerName = true;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail)) errors.customerEmail = true;
    if (phoneNumber.replace(/\D/g, '').length < 6) errors.phoneNumber = true;
    if (!termsAccepted) errors.termsAccepted = true;
    return errors;
  }

  isContinueDisabled() {
    const { phoneNumber, customerName, customerEmail, termsAccepted } = this.state;
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail);
    return phoneNumber.length < 6 || customerName.trim().length < 2 || !emailValid || !termsAccepted;
  }

  handleContinue = () => {
    const errors = this.validateFields();
    if (Object.keys(errors).length > 0) {
      this.setState({ validationErrors: errors });
      return;
    }
    this.setState({ validationErrors: {} });

    const { phonePrefix, phoneNumber, customerName, customerEmail } = this.state;
    const { i18n } = this.props;

    const {
      zoneSelected, descriptionZone, selectedLocker, lockerPrice,
      totalMinutes, slotMinutes, slotCount, pricePerSlot,
      lockerAssigned, lockerMatricula, lockerCustomer, lockerCustomerID,
      qr, qrType, qrMode, timeConfig, ticketIVA,
    } = this.props.location.state;

    this.setState({ isProcessingPayment: true });

    let language = '001';
    switch (i18n.language) {
      case 'es': language = '001'; break;
      case 'en': language = '002'; break;
      case 'it': language = '007'; break;
      case 'de': language = '005'; break;
      case 'fr': language = '004'; break;
      case 'pt': language = '009'; break;
      default: language = '002'; break;
    }

    const fullPhone = phonePrefix + phoneNumber;
    const paymentId = Math.floor(Math.random() * 1000000 + 1).toString();
    const paymentDate = new Date().toISOString();

    // Save contact data to the prebooked locker before redirecting to payment
    this.props.postTimeUpdateLockerInfo({
      LockerAssigned: lockerAssigned,
      Phone: fullPhone,
      Email: customerEmail,
      Name: customerName,
    }).catch(console.error);

    const ticketParams = `zoneSelected=${zoneSelected}&descriptionZone=${descriptionZone}&selectedLocker=${selectedLocker}&lockerPrice=${lockerPrice}&lockerAssigned=${lockerAssigned}&lockerMatricula=${lockerMatricula}&lockerCustomer=${lockerCustomer}&lockerCustomerID=${lockerCustomerID}&qrToken=${qr}&qrType=${qrType}&qrMode=${qrMode}&phoneNumber=${encodeURIComponent(fullPhone)}&customerName=${encodeURIComponent(customerName)}&customerEmail=${encodeURIComponent(customerEmail)}&totalMinutes=${totalMinutes}&slotMinutes=${slotMinutes}&slotCount=${slotCount}&pricePerSlot=${pricePerSlot}&ticketIVA=${ticketIVA || 21}&language=${i18n.language}&paymentId=${paymentId}&paymentDate=${encodeURIComponent(paymentDate)}&courtesyTime=${timeConfig?.courtesyTime || 5}`;

    // FREE: bypass Redsys entirely, go directly to ticket
    if (qrType === 'free') {
      sessionStorage.removeItem(PREBOOKING_KEY);
      if (this.unblockHistory) this.unblockHistory();
      window.removeEventListener('beforeunload', this.handleBeforeUnload);
      window.location.href = `/ticketTimeView?${ticketParams}`;
      return;
    }

    const redsysURLOK = `https://lockers-qr.vacway.es/ticketTimeView?${ticketParams}`;
    const redsysURLKO = `https://lockers-qr.vacway.es/paymentTimeError?qr=${qr}&language=${i18n.language}`;

    let redsysPetition = {
      price: lockerPrice,
      redsysURLOK,
      redsysURLKO,
      paymentId,
      merchantCode: '351639505',
      currency: '978',
      terminal: '1',
      merchantUrl: 'https://vacway.com',
      secretKey: 'xhI+J4wrx0JKkeGqJ12j2+ErLNH9OpHM',
      language,
    };

    this.props.fetchCustomer(lockerCustomerID)
      .then((customer) => {
        if (customer.response?.Redsys) {
          redsysPetition.merchantCode = customer.response.Redsys.MERCHANTCODE;
          redsysPetition.currency = customer.response.Redsys.CURRENCY;
          redsysPetition.terminal = customer.response.Redsys.TERMINAL;
          redsysPetition.merchantUrl = customer.response.Redsys.MERCHANTURL;
          redsysPetition.secretKey = customer.response.Redsys.SECRETKEY;
        }
        this.props.postRedsys(redsysPetition)
          .then((answer) => {
            const answerRedsys = answer.response;
            this.setState({
              redsysSignature: answerRedsys.Ds_Signature,
              redsysMerchant: answerRedsys.Ds_MerchantParameters,
              redsysVersion: answerRedsys.Ds_SignatureVersion,
              redsysAction: qrType === 'test'
                ? 'https://sis-t.redsys.es:25443/sis/realizarPago'
                : 'https://sis.redsys.es/sis/realizarPago',
            }, () => {
              sessionStorage.removeItem(PREBOOKING_KEY);
              if (this.unblockHistory) this.unblockHistory();
              window.removeEventListener('beforeunload', this.handleBeforeUnload);
              this.redsysFormRef.current?.submit();
            });
          })
          .catch(console.error);
      })
      .catch(console.error);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleOutsideClick);
    clearInterval(this.countdownInterval);
    if (this.unblockHistory) this.unblockHistory();
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  }

  handleBeforeUnload = (e) => {
    e.preventDefault();
    e.returnValue = '';
  }

  cancelPrebookingAndLeave = () => {
    const { lockerAssigned } = this.props.location.state;
    sessionStorage.removeItem(PREBOOKING_KEY);
    if (this.unblockHistory) this.unblockHistory();
    clearInterval(this.countdownInterval);
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    this.props.postTimeCancelPrebooking({ LockerAssigned: lockerAssigned })
      .finally(() => {
        const { pendingNavigation } = this.state;
        if (pendingNavigation) {
          history.push(pendingNavigation.location);
        } else {
          history.go(-1);
        }
      });
  }

  stayOnPage = () => {
    this.setState({ showExitDialog: false, pendingNavigation: null });
  }

  handleStartAgain = () => {
    const { lockerAssigned } = this.props.location.state;
    const { qr } = this.props.location.state;
    sessionStorage.removeItem(PREBOOKING_KEY);
    if (this.unblockHistory) this.unblockHistory();
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    this.props.postTimeCancelPrebooking({ LockerAssigned: lockerAssigned })
      .finally(() => {
        history.push(`/qrsale/${qr}`);
      });
  }

  startCountdown = () => {
    this.countdownInterval = setInterval(() => {
      if (this.state.seconds > 0) {
        this.setState(prev => ({ seconds: prev.seconds - 1 }));
      } else {
        if (this.state.minutes > 0) {
          this.setState(prev => ({ minutes: prev.minutes - 1, seconds: 59 }));
        } else {
          clearInterval(this.countdownInterval);
          this.setState({ isTimeUp: true });
          // Auto-cancel the prebooking when timer expires
          const { lockerAssigned } = this.props.location.state;
          sessionStorage.removeItem(PREBOOKING_KEY);
          this.props.postTimeCancelPrebooking({ LockerAssigned: lockerAssigned }).catch(console.error);
        }
      }
    }, 1000);
  }

  render() {
    const { t } = this.props;
    const {
      phonePrefix, phoneNumber, customerName, customerEmail, termsAccepted, prefixOpen, prefixSearch,
      isProcessingPayment, redsysSignature, redsysMerchant, redsysVersion, redsysAction,
      minutes, seconds, isTimeUp, showExitDialog, validationErrors,
    } = this.state;
    const selectedCountry = PHONE_PREFIXES.find(p => p.dial === phonePrefix) || PHONE_PREFIXES[0];
    const buttonStyle = this.getButtonStyle();
    const primaryColor = this.getPrimaryColor();

    const {
      selectedLocker, totalMinutes, lockerPrice, slotMinutes, slotCount, pricePerSlot, timeConfig,
      ticketIVA, termsLink, lockerMatricula,
    } = this.props.location.state;

    const ivaRate = Number(ticketIVA) || 21;
    const baseImponible = lockerPrice / (1 + ivaRate / 100);
    const ivaAmount = lockerPrice - baseImponible;

    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    const timeDisplay = hours > 0
      ? (mins > 0 ? `${hours}h ${mins}min` : `${hours}h`)
      : `${mins}min`;

    if (isTimeUp) {
      return (
        <div className="timeBackground">
          <AppTimeHeader />
          <div className="time-page-wrap">
            <div className="time-content time-center">
              <img src="/images/alarms.svg" alt="Timeout" className="time-timeout-img" />
              <h3>{t('Tiempo agotado')}</h3>
              <p>{t('El tiempo para completar el pago ha expirado. La taquilla reservada ha sido liberada.')}</p>
              <button
                className="time-continue-btn"
                style={buttonStyle}
                onClick={this.handleStartAgain}
              >
                {t('VOLVER A EMPEZAR')}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <React.Fragment>
      <div className="timeBackground">
        <AppTimeHeader />
        <div className="time-page-wrap">
          <div className="time-content time-locker-selector-wide">
            <div className="time-page-header">
              <div className="time-page-header-icon" style={buttonStyle}>
                <Phone size={24} strokeWidth={2} />
              </div>
              <div className="time-page-header-text">
                <h2>{t('Tus datos de contacto')}</h2>
                <p>{t('Los necesitarás para abrir y gestionar tu taquilla')}</p>
              </div>
              <span className="time-page-header-step">3 / 5</span>
            </div>

            <div className="time-prebooking-timer-card">
              <div className="time-prebooking-timer-left">
                <div className="time-prebooking-timer-display" style={{ color: minutes < 1 ? '#e74c3c' : primaryColor }}>
                  {minutes.toString().padStart(2, '0')}:{seconds.toString().padStart(2, '0')}
                </div>
                <span className="time-prebooking-timer-label">{t('Tiempo restante')}</span>
              </div>
              <div className="time-prebooking-timer-right">
                <span className="time-prebooking-timer-title">{t('Taquilla reservada para ti')}</span>
                <span className="time-prebooking-timer-desc">{t('Hemos bloqueado tu taquilla temporalmente. Completa tus datos y el pago antes de que expire el tiempo para asegurarte el alquiler.')}</span>
              </div>
            </div>

            <div className="time-phone-cols">

              {/* LEFT: customer data inputs + terms */}
              <div className="time-phone-col-left">
                <div className="time-phone-card">

                  <div className="time-phone-input-wrapper">
                    <label className="time-label">{t('Nombre completo')}</label>
                    <input
                      type="text"
                      className={`time-input-full${validationErrors.customerName ? ' time-input-error' : ''}`}
                      value={customerName}
                      onChange={e => this.setState({ customerName: e.target.value, validationErrors: { ...validationErrors, customerName: false } })}
                      placeholder={t('Tu nombre y apellidos')}
                      maxLength={80}
                    />
                    {validationErrors.customerName && (
                      <span className="time-input-error-msg">{t('Introduce tu nombre completo')}</span>
                    )}
                  </div>

                  <div className="time-phone-input-wrapper" style={{ marginTop: '14px' }}>
                    <label className="time-label">{t('Correo electrónico')}</label>
                    <input
                      type="email"
                      className={`time-input-full${validationErrors.customerEmail ? ' time-input-error' : ''}`}
                      value={customerEmail}
                      onChange={e => this.setState({ customerEmail: e.target.value, validationErrors: { ...validationErrors, customerEmail: false } })}
                      placeholder="correo@ejemplo.com"
                      maxLength={120}
                    />
                    {validationErrors.customerEmail && (
                      <span className="time-input-error-msg">{t('Introduce un correo electrónico válido')}</span>
                    )}
                  </div>

                  <div className="time-phone-input-wrapper" style={{ marginTop: '14px' }}>
                    <label className="time-label">{t('Número de teléfono')}</label>
                    <div className="time-phone-row">
                      <div className="time-prefix-picker" ref={this.prefixRef}>
                        <button
                          type="button"
                          className="time-prefix-trigger"
                          onClick={this.togglePrefixDropdown}
                        >
                          <ReactCountryFlag
                            countryCode={selectedCountry.code}
                            svg
                            style={{ width: '1.4em', height: '1.4em', borderRadius: '3px' }}
                          />
                          <span>{selectedCountry.dial}</span>
                          <i className="fas fa-chevron-down" style={{ fontSize: '0.6rem', opacity: 0.6 }}></i>
                        </button>
                        {prefixOpen && (
                          <div className="time-prefix-dropdown">
                            <input
                              type="text"
                              className="time-prefix-search"
                              placeholder={t('Buscar país...')}
                              value={prefixSearch}
                              onChange={e => this.setState({ prefixSearch: e.target.value })}
                              onClick={e => e.stopPropagation()}
                              autoFocus
                            />
                            {PHONE_PREFIXES.filter(p => {
                              const s = prefixSearch.toLowerCase();
                              return !s || p.name.toLowerCase().includes(s) || p.dial.includes(s) || p.code.toLowerCase().includes(s);
                            }).map(p => (
                              <div
                                key={p.code}
                                className={`time-prefix-option${p.dial === phonePrefix ? ' selected' : ''}`}
                                onClick={() => this.selectPrefix(p.dial)}
                              >
                                <ReactCountryFlag
                                  countryCode={p.code}
                                  svg
                                  style={{ width: '1.3em', height: '1.3em', borderRadius: '3px' }}
                                />
                                <span className="time-prefix-dial">{p.dial}</span>
                                <span className="time-prefix-name">{p.name}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <input
                        type="tel"
                        className={`time-phone-number${validationErrors.phoneNumber ? ' time-input-error' : ''}`}
                        value={phoneNumber}
                        onChange={this.handlePhoneChange}
                        placeholder="600 000 000"
                        maxLength={12}
                      />
                    </div>
                    {validationErrors.phoneNumber && (
                      <span className="time-input-error-msg">{t('Introduce un número de teléfono válido')}</span>
                    )}
                  </div>

                  <div className="time-terms">
                    <div className="time-toggle-row">
                      <span className="time-toggle-label">
                        {t('Acepto los')} <a href={termsLink || 'https://vacway.com/locker-terms.html'} target="_blank" rel="noopener noreferrer" style={{ color: primaryColor }}>{t('términos y condiciones')}</a>
                      </span>
                      <label className="time-toggle-switch">
                        <input
                          type="checkbox"
                          checked={termsAccepted}
                          onChange={this.handleTermsChange}
                        />
                        <span className="time-toggle-slider"></span>
                      </label>
                    </div>
                  </div>

                </div>
              </div>

              {/* RIGHT: summary + continue */}
              <div className="time-phone-col-right">
                <div className="time-phone-card">
                    <h5 className="time-summary-header">{t('Resumen de tu reserva')}</h5>
                    <div className="time-summary-row">
                      <span>{t('Taquilla')}</span>
                      <strong>{this.getLockerType(selectedLocker)}</strong>
                    </div>
                    <div className="time-summary-row">
                      <span>{t('Tiempo')}</span>
                      <strong>{timeDisplay}</strong>
                    </div>
                    <div className="time-summary-row">
                      <span>{t('Cortesía')}</span>
                      <strong>+{timeConfig?.courtesyTime || 5} min</strong>
                    </div>
                    <div className="time-summary-divider"></div>
                    <div className="time-summary-row">
                      <span>{t('Base imponible')}</span>
                      <span>{baseImponible.toFixed(2)} €</span>
                    </div>
                    <div className="time-summary-row">
                      <span>{t('IVA')} ({ivaRate}%)</span>
                      <span>{ivaAmount.toFixed(2)} €</span>
                    </div>
                    <div className="time-summary-divider"></div>
                    <div className="time-summary-row time-summary-total">
                      <span>{t('Total')}</span>
                      <strong>{lockerPrice.toFixed(2)} €</strong>
                    </div>

                  <button
                    className="time-continue-btn"
                    style={buttonStyle}
                    onClick={this.handleContinue}
                    disabled={this.isContinueDisabled() || isProcessingPayment}
                  >
                    {isProcessingPayment ? t('Procesando...') : t('CONTINUAR AL PAGO')}
                  </button>
                </div>
              </div>
            </div>

            <div className="time-disclaimer">
              <i className="fas fa-shield-alt" style={{ marginRight: '6px', opacity: 0.5 }}></i>
              <span>{t('*Los datos personales recabados no se usarán en ningún caso para ofrecer comunicados comerciales de ningún tipo si el cliente no se suscribe a la newsletter.')}</span>
            </div>

            <form
              ref={this.redsysFormRef}
              action={redsysAction}
              method="POST"
              style={{ display: 'none' }}
            >
              <input type="hidden" name="Ds_SignatureVersion" value={redsysVersion || ''} />
              <input type="hidden" name="Ds_MerchantParameters" value={redsysMerchant || ''} />
              <input type="hidden" name="Ds_Signature" value={redsysSignature || ''} />
            </form>

          </div>
        </div>
        <StepBarTime step={3} />
      </div>

      {showExitDialog && (
        <div className="time-exit-overlay">
          <div className="time-exit-dialog">
            <div className="time-exit-dialog-icon">⚠️</div>
            <h4 className="time-exit-dialog-title">{t('¿Seguro que quieres salir?')}</h4>
            <p className="time-exit-dialog-body">{t('Si abandonas ahora, tu taquilla reservada quedará libre y otro usuario podrá alquilarla. El temporizador se cancelará.')}</p>
            <div className="time-exit-dialog-actions">
              <button className="time-exit-btn-stay" style={buttonStyle} onClick={this.stayOnPage}>
                {t('Continuar con el alquiler')}
              </button>
              <button className="time-exit-btn-leave" onClick={this.cancelPrebookingAndLeave}>
                {t('Cancelar y salir')}
              </button>
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
    );
  }
}

const mapStateToProps = state => ({
  auth: true,
  qrData: state.qrs.qr
})

const mapDispatchToProps = dispatch => ({
  push: bindActionCreators(push, dispatch),
  postRedsys: bindActionCreators(postRedsys, dispatch),
  fetchCustomer: bindActionCreators(fetchCustomer, dispatch),
  postTimeCancelPrebooking: bindActionCreators(postTimeCancelPrebooking, dispatch),
  postTimeUpdateLockerInfo: bindActionCreators(postTimeUpdateLockerInfo, dispatch),
})

export default withNamespaces()(connect(mapStateToProps, mapDispatchToProps)(PhoneTimeView));
